/**
 * Design tokens for vedic astrology pages.
 * Used by VedicThemeProvider — access via `props.theme.*` in styled-components.
 * Band names match backend vocabulary in internal/astro/tools/display/display.go.
 */
export const tokens = {
  colors: {
    primary: '#6c63ff',
    bands: {
      exceptional: '#B8860B', // was #FFD700 — WCAG fix (D10)
      strong: '#4caf50',
      moderate: '#2196f3',
      weak: '#ff9800',
      challenging: '#f44336',
    },
    pass: '#4caf50',
    fail: '#f44336',
    error: { bg: '#ffebee', text: '#c62828' },
    surface: '#ffffff',
    background: '#f5f5f5',
    text: { primary: '#212121', secondary: '#757575', muted: '#6d6d6d', label: '#8B6914' }, // muted was #9e9e9e — WCAG fix (D9)
    border: '#e0e0e0',
    gold:     { primary: '#B8860B', accent: '#B8860B' },
    tone: {
      neutral: { bg: 'transparent', text: 'inherit' },
      red:     { bg: 'rgba(231,76,60,0.08)',  text: '#C0392B' },
      green:   { bg: 'rgba(39,174,96,0.08)',  text: '#1E8449' },
    },
    footer: {
      auspicious:   { bg: 'rgba(39,174,96,0.10)',  dot: '#27AE60' },
      inauspicious: { bg: 'rgba(231,76,60,0.10)',   dot: '#E74C3C' },
      neutral:      { bg: 'rgba(127,127,127,0.06)', dot: '#9E9E9E' },
    },
    festival: { bg: 'rgba(184,134,11,0.10)', text: '#B8860B', border: '#B8860B' },
    siddhanta: { bg: '#F5F5F5', text: '#757575', border: '#E0E0E0' },
    modal: { backdrop: 'rgba(0,0,0,0.5)' },
    time:  { mono: '#757575' },
    accentTones: { good: '#1E8449', bad: '#C0392B', neutral: '#8B6914' },
  },
  // spacing: integer values in px. Usage: `${p => p.theme.spacing[4]}px`
  // Index: 0=0, 1=4, 2=8, 3=12, 4=16, 5=24, 6=32, 7=48, 8=64
  spacing: [0, 4, 8, 12, 16, 24, 32, 48, 64],
  radius: { sm: 4, md: 8, lg: 12 },
  shadow: {
    card: '0 3px 10px rgba(0,0,0,0.1)',
    elevated: '0 6px 20px rgba(0,0,0,0.12)',
    none: 'none',
  },
  typography: {
    fontFamily: "'Roboto', 'Segoe UI', sans-serif",
    monoFamily: "'Roboto Mono', 'Courier New', monospace",
    sizes: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.1rem', xl: '1.5rem', xxl: '2rem' },
    weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
  breakpoints: { mobile: 576, tablet: 768, desktop: 992 },
};

export default tokens;

/**
 * Dark theme tokens for panchangam page.
 * Mirrors the shape of `tokens` with dark-appropriate values.
 * UX spec: phase1a-ux.md section 5 (Pambu Paper under oil lamp).
 */
export const darkTokens = {
  colors: {
    primary: '#6c63ff',
    bands: {
      exceptional: '#D4B870',
      strong: '#5BD88A',
      moderate: '#2196f3',
      weak: '#ff9800',
      challenging: '#F27D6E',
    },
    pass: '#5BD88A',
    fail: '#F27D6E',
    error: { bg: 'rgba(242,125,110,0.12)', text: '#F27D6E' },
    surface: '#2A251D',
    background: '#1E1B14',
    text: { primary: '#F0E6D2', secondary: '#A89B85', muted: '#958C7A', label: '#D4B870' },
    border: '#3D352A',
    gold:     { primary: '#D4B870', accent: '#E8B450' },
    tone: {
      neutral: { bg: 'transparent', text: 'inherit' },
      red:     { bg: 'rgba(242,125,110,0.12)', text: '#F27D6E' },
      green:   { bg: 'rgba(91,216,138,0.12)',   text: '#5BD88A' },
    },
    footer: {
      auspicious:   { bg: 'rgba(39,174,96,0.15)',  dot: '#5BD88A' },
      inauspicious: { bg: 'rgba(231,76,60,0.15)',   dot: '#F27D6E' },
      neutral:      { bg: 'rgba(127,127,127,0.10)', dot: '#958C7A' },
    },
    festival: { bg: 'rgba(212,184,112,0.15)', text: '#D4B870', border: '#D4B870' },
    siddhanta: { bg: '#2A251D', text: '#A89B85', border: '#3D352A' },
    modal: { backdrop: 'rgba(0,0,0,0.7)' },
    time:  { mono: '#D4B870' },
    accentTones: { good: '#5BD88A', bad: '#F27D6E', neutral: '#D4B870' },
  },
  spacing: [0, 4, 8, 12, 16, 24, 32, 48, 64],
  radius: { sm: 4, md: 8, lg: 12 },
  shadow: {
    card: '0 3px 10px rgba(0,0,0,0.3)',
    elevated: '0 6px 20px rgba(0,0,0,0.35)',
    none: 'none',
  },
  typography: {
    fontFamily: "'Roboto', 'Segoe UI', sans-serif",
    monoFamily: "'Roboto Mono', 'Courier New', monospace",
    sizes: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem', lg: '1.1rem', xl: '1.5rem', xxl: '2rem' },
    weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
  breakpoints: { mobile: 576, tablet: 768, desktop: 992 },
};
